<template>
  <v-container fluid class="pa-0 pa-md-4 py-12 py-md-4">
    <v-card class="w-100 border" elevation="0">
      <v-card-title
        class="d-flex justify-space-between align-center flex-wrap ga-3 text-h5 font-weight-bold"
      >
        <div class="page-header-copy">
          <div class="d-flex align-center flex-wrap ga-1">
            <div class="page-header-title">Publish Preview</div>
            <v-tooltip
              v-if="simulationId"
              :text="simulationIdTooltipText"
              location="top"
            >
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  class="text-medium-emphasis simulation-id-tooltip-btn"
                  icon
                  variant="text"
                  size="small"
                  density="comfortable"
                  :aria-label="simulationIdCopyButtonAriaLabel"
                  data-testid="detailed-impact-simulation-id"
                  :data-simulation-id="simulationId"
                  @mouseenter="simulationIdButtonHovered = true"
                  @mouseleave="onSimulationIdButtonMouseLeave"
                  @click="copySimulationIdToClipboard($event)"
                >
                  <v-icon
                    :icon="
                      simulationIdCopyAck
                        ? 'mdi-check'
                        : simulationIdButtonHovered
                          ? 'mdi-content-copy'
                          : 'mdi-information-outline'
                    "
                    size="18"
                  />
                </v-btn>
              </template>
            </v-tooltip>
          </div>
          <div
            class="text-subtitle-2 text-medium-emphasis font-weight-regular"
          >
            What will happen if you publish?
          </div>
        </div>
        <div class="page-header-actions">
          <v-btn
            color="primary"
            variant="flat"
            :loading="previewStore.startingPreview"
            :disabled="isProcessing || noUnpublishedChanges"
            @click="runPreview"
            data-testid="publish-preview-run"
          >
            Run Preview
          </v-btn>
        </div>
      </v-card-title>

      <v-card-text class="pt-4">
        <v-alert
          v-if="isProcessing"
          type="info"
          variant="tonal"
          class="mb-4"
          data-testid="publish-preview-processing-banner"
        >
          <div class="d-flex align-center justify-space-between">
            <div>
              <div class="text-subtitle-2 font-weight-bold">Publish Preview is processing.</div>
              <div class="text-body-2">
                You can keep working, but changes will require a new preview.
                <span v-if="progressLabel">{{ progressLabel }}</span>
              </div>
            </div>
            <v-progress-circular indeterminate color="info" size="32" />
          </div>
          <v-progress-linear
            v-if="progressPercent !== null"
            :model-value="progressPercent"
            height="6"
            rounded
            color="info"
            class="mt-3"
          />
        </v-alert>

        <v-alert
          v-else-if="isStale"
          type="warning"
          variant="tonal"
          class="mb-4"
          data-testid="publish-preview-stale-banner"
        >
          {{ stalePreviewMessage }}
        </v-alert>

        <v-alert
          v-else-if="isFailed"
          type="error"
          variant="tonal"
          class="mb-4"
          data-testid="publish-preview-failed-banner"
        >
          {{ errorMessage || 'Publish Preview failed. Please try again.' }}
        </v-alert>

        <v-card
          v-if="noUnpublishedChanges"
          class="border pa-8 text-center"
          elevation="0"
          data-testid="publish-preview-no-unpublished"
        >
          <div class="text-h6 mb-2">No unpublished changes to preview</div>
          <div class="text-body-2 text-medium-emphasis">
            Make a draft change first, then run Publish Preview to review the impact before publishing.
          </div>
        </v-card>

        <v-card
          v-else-if="!result && (isProcessing || isInitialLoading)"
          class="border pa-8 text-center"
          elevation="0"
        >
          <v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
          <div class="text-body-1">
            <template v-if="isProcessing">Running publish preview...</template>
            <template v-else>Loading...</template>
          </div>
        </v-card>

        <template v-if="result">
          <PendingVehicleDraftManualOverridesCard class="mb-4" />
          <v-alert
            v-if="previewMissingSimulationId"
            type="error"
            variant="tonal"
            class="mb-4"
            data-testid="publish-preview-v2-unexpected"
          >
            Preview data is incomplete (missing simulation id). Run Preview again.
          </v-alert>
          <DetailedImpactPanel v-else-if="detailedImpactReady" />
          <v-card
            v-else-if="v2DetailedImpactLoading"
            class="border pa-8 text-center"
            elevation="0"
            data-testid="publish-preview-v2-loading"
          >
            <v-progress-circular indeterminate color="primary" size="48" class="mb-4" />
            <div class="text-body-1">Loading detailed preview…</div>
          </v-card>
          <v-alert
            v-else-if="v2DetailedImpactUnavailable"
            type="warning"
            variant="tonal"
            class="mb-4"
            data-testid="publish-preview-v2-unavailable"
          >
            Detailed preview isn’t available for this run. Try Run Preview again. If it keeps
            happening, contact support.
          </v-alert>
        </template>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useDetailedImpactStore } from '@/stores/useDetailedImpactStore'
import { usePublishPreviewStore } from '@/stores/usePublishPreviewStore'
import { fetchUnpublishedChangesSummary } from '@/services/publishService'
import { makeAlert } from '@/utils'
import DetailedImpactPanel from '@/components/publishPreview/DetailedImpactPanel.vue'
import PendingVehicleDraftManualOverridesCard from '@/components/PendingVehicleDraftManualOverridesCard.vue'

const accountSettings = useAccountSettingsStore()
const detailedImpactStore = useDetailedImpactStore()
const previewStore = usePublishPreviewStore()

const endpoint = computed(() => import.meta.env.VITE_ISE_BACKEND_URL as string)
const isInitialLoading = ref(false)
const unpublishedChecked = ref(false)
const hasUnpublishedChanges = ref(true)

const result = computed(() => previewStore.result)
const simulationId = computed(() => result.value?.simulationId ?? null)

const simulationIdButtonHovered = ref(false)
const simulationIdCopyAck = ref(false)

const simulationIdTooltipText = computed(() => {
  const id = simulationId.value
  if (!id) return ''
  if (simulationIdCopyAck.value) return 'Copied!'
  return `Simulation ID: ${id} — Click to copy`
})

const simulationIdCopyButtonAriaLabel = computed(() =>
  simulationIdCopyAck.value ? 'Simulation ID copied to clipboard' : 'Copy simulation ID',
)

function onSimulationIdButtonMouseLeave() {
  simulationIdButtonHovered.value = false
  simulationIdCopyAck.value = false
}

async function copySimulationIdToClipboard(event?: MouseEvent) {
  event?.stopPropagation()
  const id = simulationId.value
  if (!id) return
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(id)
      simulationIdCopyAck.value = true
    }
  } catch (e) {
    console.warn('Failed to copy simulation ID', e)
  }
}

const previewMissingSimulationId = computed(() => !!result.value && !simulationId.value)

const detailedImpactReady = computed(
  () =>
    detailedImpactStore.gateProbed &&
    detailedImpactStore.gateAvailable &&
    detailedImpactStore.probedSimulationId === simulationId.value,
)

const v2GateSettledForCurrentSim = computed(() => {
  const sim = simulationId.value
  if (!sim) return false
  return detailedImpactStore.gateProbed && detailedImpactStore.probedSimulationId === sim
})

const v2DetailedImpactLoading = computed(
  () =>
    !!result.value &&
    !!simulationId.value &&
    !previewMissingSimulationId.value &&
    !v2GateSettledForCurrentSim.value,
)

const v2DetailedImpactUnavailable = computed(
  () =>
    !!result.value &&
    !previewMissingSimulationId.value &&
    v2GateSettledForCurrentSim.value &&
    !detailedImpactStore.gateAvailable,
)

const isProcessing = computed(
  () => previewStore.status === 'processing' || previewStore.startingPreview,
)
const noUnpublishedChanges = computed(() => unpublishedChecked.value && !hasUnpublishedChanges.value)
const isStale = computed(() => previewStore.status === 'stale')
const isFailed = computed(() => previewStore.status === 'failed')
const errorMessage = computed(() => previewStore.errorMessage)
const stalePreviewMessage = computed(() => {
  const reasons = new Set(previewStore.staleReasons || [])
  const messages: string[] = []
  if (reasons.has('config')) {
    messages.push('Settings changed during preview.')
  }
  if (reasons.has('feed')) {
    messages.push('A newer feed arrived during preview.')
  }
  if (messages.length === 0) {
    messages.push('Preview completed, but it no longer matches the current inputs.')
  }
  return `${messages.join(' ')} Run Preview again to see current impact.`
})

const progressPercent = computed(() => {
  const p = previewStore.progress?.percent
  return typeof p === 'number' ? p : null
})

const progressLabel = computed(() => {
  const p = previewStore.progress
  if (!p) return ''
  if (p.total > 0) {
    return `(Checking ${p.processed} of ${p.total} vehicles)`
  }
  return ''
})

const ensureContext = (): { jazelId: string; sessionToken: string } | null => {
  if (!accountSettings.accountId || !accountSettings.sessionToken) {
    makeAlert('Missing account/session context for preview.', 'error')
    return null
  }
  return {
    jazelId: accountSettings.accountId,
    sessionToken: accountSettings.sessionToken,
  }
}

async function refreshUnpublishedGate(ctx: { jazelId: string; sessionToken: string }): Promise<boolean> {
  try {
    const summary = await fetchUnpublishedChangesSummary(endpoint.value, ctx.jazelId, ctx.sessionToken)
    const hasChanges = summary?.hasUnpublished === true
    hasUnpublishedChanges.value = hasChanges
    unpublishedChecked.value = true

    if (!hasChanges) {
      previewStore.reset()
      detailedImpactStore.reset()
    }

    return hasChanges
  } catch (error) {
    console.warn('PublishPreview.unpublishedSummaryFailed', error)
    unpublishedChecked.value = false
    hasUnpublishedChanges.value = true
    return true
  }
}

async function runDetailedImpactProbe(simId: string) {
  if (!accountSettings.sessionToken) return
  if (!endpoint.value) return
  try {
    await detailedImpactStore.probeForSimulation({
      endpoint: endpoint.value,
      simulationId: simId,
      sessionToken: accountSettings.sessionToken,
    })
  } catch (error) {
    console.warn('publishPreview.v2.probeThrew', error)
    detailedImpactStore.setV2GateUnavailableAfterProbeError(simId)
  }
}

watch(simulationId, (next, prev) => {
  if (next !== prev) {
    simulationIdButtonHovered.value = false
    simulationIdCopyAck.value = false
  }
  if (!next || next === prev) return
  detailedImpactStore.resetTransientState()
  void runDetailedImpactProbe(next)
}, { immediate: true })

const runPreview = async () => {
  const ctx = ensureContext()
  if (!ctx) return
  const hasChanges = await refreshUnpublishedGate(ctx)
  if (!hasChanges) return

  const startResult = await previewStore.runPreview({
    endpoint: endpoint.value,
    jazelId: ctx.jazelId,
    sessionToken: ctx.sessionToken,
    options: {
      maxVins: 5000,
      sampleStrategy: 'first',
      vinPageSize: 100,
      vinPageOffset: 0,
    },
  })
  if (!startResult) {
    if (previewStore.errorMessage) {
      makeAlert(previewStore.errorMessage, 'error')
    }
    return
  }
  if (startResult.reused) {
    makeAlert('Attached to in-flight Publish Preview job.', 'info')
  } else {
    makeAlert('Publish Preview started. You can keep working.', 'info')
  }
}

onMounted(async () => {
  const ctx = ensureContext()
  if (!ctx) return
  isInitialLoading.value = true
  try {
    const hasChanges = await refreshUnpublishedGate(ctx)
    if (!hasChanges) return

    const latest = await previewStore.refreshLatest({
      endpoint: endpoint.value,
      jazelId: ctx.jazelId,
      sessionToken: ctx.sessionToken,
    })

    if (
      latest &&
      latest.latestSimulationId &&
      latest.latestStatus === 'complete' &&
      latest.isPreviewValid &&
      !previewStore.result
    ) {
      const { getPublishPreviewStatus } = await import('@/services/publishPreviewService')
      try {
        const payload = await getPublishPreviewStatus({
          endpoint: endpoint.value,
          simulationId: latest.latestSimulationId,
          sessionToken: ctx.sessionToken,
        })
        if (payload.status === 'complete' && payload.result) {
          previewStore.result = payload.result
        }
      } catch (error) {
        console.warn('PublishPreview.hydrateLatestFailed', error)
      }
    }

    if (!latest || latest.latestStatus === null) {
      await runPreview()
    }
  } finally {
    isInitialLoading.value = false
  }
})
</script>
